import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Switch,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  CopyOutlined,
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store';
import {
  fetchTaskMetadata,
  refreshTaskMetadataRegistry,
  saveTaskCategory,
  saveTaskDefinition,
  saveTaskSubtaskRelation,
} from '../../store/slices/taskMetadataSlice';
import {
  TaskCategory,
  TaskCategoryPayload,
  TaskDefinition,
  TaskDefinitionPayload,
  TaskSubtaskRelation,
  TaskSubtaskRelationPayload,
  TaskRelationType,
} from '../../types';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';

const { Text } = Typography;

type ActiveTabKey = 'definitions' | 'categories' | 'relations';

const TASK_KEY_PATTERN = /^[a-z0-9_]+$/;
const CATEGORY_CODE_PATTERN = /^[A-Za-z0-9_-]+$/;

const relationTypeOptions: Array<{ label: string; value: TaskRelationType }> = [
  { label: 'NEEDOPT', value: 'NEEDOPT' },
  { label: 'QWEN_NEXT', value: 'QWEN_NEXT' },
  { label: 'LTX_PREPROCESS', value: 'LTX_PREPROCESS' },
];

const formatDateTime = (value?: number | string) => {
  if (!value && value !== 0) {
    return '-';
  }

  if (typeof value === 'number') {
    const normalizedValue = value < 1_000_000_000_000 ? value * 1000 : value;
    return new Date(normalizedValue).toLocaleString();
  }

  const timestamp = Date.parse(value);
  return Number.isNaN(timestamp) ? value : new Date(timestamp).toLocaleString();
};

const renderBooleanTag = (value: boolean, trueText = '启用', falseText = '禁用') => (
  <Tag color={value ? 'success' : 'default'}>{value ? trueText : falseText}</Tag>
);

const TaskMetadataManagement: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { taskDefinitions, taskCategories, taskSubtaskRelations, loading, saving, refreshing } =
    useSelector((state: RootState) => state.taskMetadata);
  const { autoRefresh, refreshInterval } = useSelector((state: RootState) => state.settings);
  const currentUser = useSelector((state: RootState) => state.auth.user);

  const [activeTab, setActiveTab] = useState<ActiveTabKey>('definitions');
  const [definitionModalVisible, setDefinitionModalVisible] = useState(false);
  const [categoryModalVisible, setCategoryModalVisible] = useState(false);
  const [relationModalVisible, setRelationModalVisible] = useState(false);
  const [editingDefinition, setEditingDefinition] = useState<TaskDefinition | null>(null);
  const [editingCategory, setEditingCategory] = useState<TaskCategory | null>(null);
  const [editingRelation, setEditingRelation] = useState<TaskSubtaskRelation | null>(null);

  const [definitionForm] = Form.useForm<TaskDefinitionPayload>();
  const [categoryForm] = Form.useForm<TaskCategoryPayload>();
  const [relationForm] = Form.useForm<TaskSubtaskRelationPayload>();

  const relationType = Form.useWatch('relationType', relationForm);

  const operatorInitialValue = currentUser?.username || 'admin';
  const isMobile = window.innerWidth <= 768;

  const refreshData = useCallback(() => {
    dispatch(fetchTaskMetadata());
  }, [dispatch]);

  useAutoRefresh({
    interval: refreshInterval,
    enabled: autoRefresh,
    onRefresh: refreshData,
  });

  useEffect(() => {
    refreshData();
  }, [refreshData]);

  const taskDefinitionOptions = useMemo(
    () =>
      taskDefinitions.map((item) => ({
        label: `${item.taskName} (${item.taskType})`,
        value: item.taskType,
      })),
    [taskDefinitions]
  );

  const taskDefinitionNameMap = useMemo(
    () =>
      taskDefinitions.reduce<Record<number, string>>((map, item) => {
        map[item.taskType] = item.taskName;
        return map;
      }, {}),
    [taskDefinitions]
  );

  const categoryOptions = useMemo(
    () =>
      taskCategories.map((item) => ({
        label: `${item.categoryName} (${item.categoryCode})`,
        value: item.categoryCode,
      })),
    [taskCategories]
  );

  const closeDefinitionModal = () => {
    setDefinitionModalVisible(false);
    setEditingDefinition(null);
    definitionForm.resetFields();
  };

  const closeCategoryModal = () => {
    setCategoryModalVisible(false);
    setEditingCategory(null);
    categoryForm.resetFields();
  };

  const closeRelationModal = () => {
    setRelationModalVisible(false);
    setEditingRelation(null);
    relationForm.resetFields();
  };

  const openCreateDefinitionModal = () => {
    setEditingDefinition(null);
    definitionForm.setFieldsValue({
      priorityQueueEnabled: false,
      enabled: true,
      operator: operatorInitialValue,
    });
    setDefinitionModalVisible(true);
  };

  const openEditDefinitionModal = (record: TaskDefinition) => {
    setEditingDefinition(record);
    definitionForm.setFieldsValue({
      id: record.id,
      taskType: record.taskType,
      taskKey: record.taskKey,
      taskName: record.taskName,
      description: record.description,
      categoryCode: record.categoryCode || undefined,
      priorityQueueEnabled: record.priorityQueueEnabled,
      enabled: record.enabled,
      operator: currentUser?.username || record.updatedBy || record.createdBy || 'admin',
    });
    setDefinitionModalVisible(true);
  };

  const openDuplicateDefinitionModal = (record: TaskDefinition) => {
    setEditingDefinition(null);
    definitionForm.setFieldsValue({
      taskType: undefined as unknown as number,
      taskKey: '',
      taskName: `${record.taskName}_copy`,
      description: record.description,
      categoryCode: record.categoryCode || undefined,
      priorityQueueEnabled: record.priorityQueueEnabled,
      enabled: record.enabled,
      operator: currentUser?.username || record.updatedBy || record.createdBy || 'admin',
    });
    setDefinitionModalVisible(true);
  };

  const openCreateCategoryModal = () => {
    setEditingCategory(null);
    categoryForm.setFieldsValue({
      priorityEnabled: false,
      enabled: true,
      operator: operatorInitialValue,
    });
    setCategoryModalVisible(true);
  };

  const openEditCategoryModal = (record: TaskCategory) => {
    setEditingCategory(record);
    categoryForm.setFieldsValue({
      id: record.id,
      categoryCode: record.categoryCode,
      categoryName: record.categoryName,
      description: record.description,
      queueSuffix: record.queueSuffix || undefined,
      priorityEnabled: record.priorityEnabled,
      enabled: record.enabled,
      operator: currentUser?.username || record.updatedBy || record.createdBy || 'admin',
    });
    setCategoryModalVisible(true);
  };

  const openCreateRelationModal = () => {
    setEditingRelation(null);
    relationForm.setFieldsValue({
      resetTriggerAfterCompletion: false,
      copyImg2: false,
      enabled: true,
      operator: operatorInitialValue,
    });
    setRelationModalVisible(true);
  };

  const openEditRelationModal = (record: TaskSubtaskRelation) => {
    setEditingRelation(record);
    relationForm.setFieldsValue({
      id: record.id,
      relationName: record.relationName,
      parentTaskType: record.parentTaskType,
      subtaskTaskType: record.subtaskTaskType,
      relationType: record.relationType,
      triggerField: record.triggerField,
      triggerValue: record.triggerValue,
      processedFlagField: record.processedFlagField,
      resultTargetField: record.resultTargetField,
      resetTriggerAfterCompletion: record.resetTriggerAfterCompletion,
      copyImg2: record.copyImg2,
      promptText: record.promptText,
      promptTextWhenNeedopt: record.promptTextWhenNeedopt,
      enabled: record.enabled,
      operator: currentUser?.username || record.updatedBy || record.createdBy || 'admin',
    });
    setRelationModalVisible(true);
  };

  const openDuplicateRelationModal = (record: TaskSubtaskRelation) => {
    setEditingRelation(null);
    relationForm.setFieldsValue({
      relationName: `${record.relationName}_copy`,
      parentTaskType: record.parentTaskType,
      subtaskTaskType: record.subtaskTaskType,
      relationType: record.relationType,
      triggerField: record.triggerField,
      triggerValue: record.triggerValue,
      processedFlagField: record.processedFlagField,
      resultTargetField: record.resultTargetField,
      resetTriggerAfterCompletion: record.resetTriggerAfterCompletion,
      copyImg2: record.copyImg2,
      promptText: record.promptText,
      promptTextWhenNeedopt: record.promptTextWhenNeedopt,
      enabled: record.enabled,
      operator: currentUser?.username || record.updatedBy || record.createdBy || 'admin',
    });
    setRelationModalVisible(true);
  };

  const handleSaveDefinition = async (values: TaskDefinitionPayload) => {
    try {
      await dispatch(
        saveTaskDefinition({
          ...values,
          categoryCode: values.categoryCode || undefined,
        })
      ).unwrap();
      message.success(editingDefinition ? '任务定义已更新' : '任务定义已创建');
      closeDefinitionModal();
      refreshData();
    } catch (error) {
      message.error('保存任务定义失败');
    }
  };

  const handleSaveCategory = async (values: TaskCategoryPayload) => {
    try {
      await dispatch(
        saveTaskCategory({
          ...values,
          queueSuffix: values.queueSuffix || undefined,
        })
      ).unwrap();
      message.success(editingCategory ? '任务分类已更新' : '任务分类已创建');
      closeCategoryModal();
      refreshData();
    } catch (error) {
      message.error('保存任务分类失败');
    }
  };

  const handleSaveRelation = async (values: TaskSubtaskRelationPayload) => {
    try {
      await dispatch(
        saveTaskSubtaskRelation({
          ...values,
          triggerField: values.triggerField || undefined,
          triggerValue: values.triggerValue || undefined,
          processedFlagField: values.processedFlagField || undefined,
          resultTargetField: values.resultTargetField || undefined,
          promptText: values.promptText || undefined,
          promptTextWhenNeedopt: values.promptTextWhenNeedopt || undefined,
        })
      ).unwrap();
      message.success(editingRelation ? '子任务关系已更新' : '子任务关系已创建');
      closeRelationModal();
      refreshData();
    } catch (error) {
      message.error('保存子任务关系失败');
    }
  };

  const handleRefreshRegistry = async () => {
    try {
      const result = await dispatch(refreshTaskMetadataRegistry()).unwrap();
      message.success(result || '任务元数据注册中心已刷新');
    } catch (error) {
      message.error('刷新任务元数据注册中心失败');
    }
  };

  const toggleTaskDefinitionEnabled = async (record: TaskDefinition, enabled: boolean) => {
    try {
      await dispatch(
        saveTaskDefinition({
          id: record.id,
          taskType: record.taskType,
          taskKey: record.taskKey,
          taskName: record.taskName,
          description: record.description,
          categoryCode: record.categoryCode || undefined,
          priorityQueueEnabled: record.priorityQueueEnabled,
          enabled,
          operator: currentUser?.username || record.updatedBy || record.createdBy || 'admin',
        })
      ).unwrap();
      message.success(enabled ? '任务定义已启用' : '任务定义已禁用');
      refreshData();
    } catch (error) {
      message.error('更新任务定义状态失败');
    }
  };

  const toggleTaskCategoryEnabled = async (record: TaskCategory, enabled: boolean) => {
    try {
      await dispatch(
        saveTaskCategory({
          id: record.id,
          categoryCode: record.categoryCode,
          categoryName: record.categoryName,
          description: record.description,
          queueSuffix: record.queueSuffix || undefined,
          priorityEnabled: record.priorityEnabled,
          enabled,
          operator: currentUser?.username || record.updatedBy || record.createdBy || 'admin',
        })
      ).unwrap();
      message.success(enabled ? '任务分类已启用' : '任务分类已禁用');
      refreshData();
    } catch (error) {
      message.error('更新任务分类状态失败');
    }
  };

  const toggleTaskRelationEnabled = async (record: TaskSubtaskRelation, enabled: boolean) => {
    try {
      await dispatch(
        saveTaskSubtaskRelation({
          id: record.id,
          relationName: record.relationName,
          parentTaskType: record.parentTaskType,
          subtaskTaskType: record.subtaskTaskType,
          relationType: record.relationType,
          triggerField: record.triggerField,
          triggerValue: record.triggerValue,
          processedFlagField: record.processedFlagField,
          resultTargetField: record.resultTargetField,
          resetTriggerAfterCompletion: record.resetTriggerAfterCompletion,
          copyImg2: record.copyImg2,
          promptText: record.promptText,
          promptTextWhenNeedopt: record.promptTextWhenNeedopt,
          enabled,
          operator: currentUser?.username || record.updatedBy || record.createdBy || 'admin',
        })
      ).unwrap();
      message.success(enabled ? '子任务关系已启用' : '子任务关系已禁用');
      refreshData();
    } catch (error) {
      message.error('更新子任务关系状态失败');
    }
  };

  const definitionColumns = [
    {
      title: '任务类型',
      dataIndex: 'taskType',
      key: 'taskType',
      width: 120,
      sorter: (a: TaskDefinition, b: TaskDefinition) => a.taskType - b.taskType,
    },
    {
      title: '任务 Key',
      dataIndex: 'taskKey',
      key: 'taskKey',
      width: 180,
    },
    {
      title: '任务名称',
      dataIndex: 'taskName',
      key: 'taskName',
      width: 180,
    },
    {
      title: '分类',
      dataIndex: 'categoryCode',
      key: 'categoryCode',
      width: 150,
      render: (value: string | null | undefined) => value || '-',
    },
    {
      title: '优先级队列',
      dataIndex: 'priorityQueueEnabled',
      key: 'priorityQueueEnabled',
      width: 110,
      render: (value: boolean) => renderBooleanTag(value, '开启', '关闭'),
    },
    {
      title: '启用',
      dataIndex: 'enabled',
      key: 'enabled',
      width: 100,
      render: (_: boolean, record: TaskDefinition) => (
        <Switch
          checked={record.enabled}
          size="small"
          onChange={(checked) => toggleTaskDefinitionEnabled(record, checked)}
        />
      ),
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (value: string | undefined) => value || '-',
    },
    {
      title: '更新人',
      dataIndex: 'updatedBy',
      key: 'updatedBy',
      width: 120,
      render: (value: string | undefined, record: TaskDefinition) => value || record.createdBy || '-',
    },
    {
      title: '更新时间',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 180,
      render: (value: number | string | undefined) => formatDateTime(value),
    },
    {
      title: '操作',
      key: 'action',
      width: 180,
      fixed: 'right' as const,
      render: (_: unknown, record: TaskDefinition) => (
        <Space size={4}>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openEditDefinitionModal(record)}>
            编辑
          </Button>
          <Button type="link" size="small" icon={<CopyOutlined />} onClick={() => openDuplicateDefinitionModal(record)}>
            复制
          </Button>
        </Space>
      ),
    },
  ];

  const categoryColumns = [
    {
      title: '分类编码',
      dataIndex: 'categoryCode',
      key: 'categoryCode',
      width: 160,
    },
    {
      title: '分类名称',
      dataIndex: 'categoryName',
      key: 'categoryName',
      width: 180,
    },
    {
      title: '队列后缀',
      dataIndex: 'queueSuffix',
      key: 'queueSuffix',
      width: 150,
      render: (value: string | null | undefined) => value || '-',
    },
    {
      title: '优先级队列',
      dataIndex: 'priorityEnabled',
      key: 'priorityEnabled',
      width: 110,
      render: (value: boolean) => renderBooleanTag(value, '开启', '关闭'),
    },
    {
      title: '启用',
      dataIndex: 'enabled',
      key: 'enabled',
      width: 100,
      render: (_: boolean, record: TaskCategory) => (
        <Switch
          checked={record.enabled}
          size="small"
          onChange={(checked) => toggleTaskCategoryEnabled(record, checked)}
        />
      ),
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (value: string | undefined) => value || '-',
    },
    {
      title: '更新人',
      dataIndex: 'updatedBy',
      key: 'updatedBy',
      width: 120,
      render: (value: string | undefined, record: TaskCategory) => value || record.createdBy || '-',
    },
    {
      title: '更新时间',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 180,
      render: (value: number | string | undefined) => formatDateTime(value),
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      fixed: 'right' as const,
      render: (_: unknown, record: TaskCategory) => (
        <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openEditCategoryModal(record)}>
          编辑
        </Button>
      ),
    },
  ];

  const relationColumns = [
    {
      title: '关系名称',
      dataIndex: 'relationName',
      key: 'relationName',
      width: 180,
    },
    {
      title: '父任务',
      dataIndex: 'parentTaskType',
      key: 'parentTaskType',
      width: 180,
      render: (value: number) => `${taskDefinitionNameMap[value] || '未知任务'} (${value})`,
    },
    {
      title: '子任务',
      dataIndex: 'subtaskTaskType',
      key: 'subtaskTaskType',
      width: 180,
      render: (value: number) => `${taskDefinitionNameMap[value] || '未知任务'} (${value})`,
    },
    {
      title: '关系类型',
      dataIndex: 'relationType',
      key: 'relationType',
      width: 140,
      render: (value: TaskRelationType) => <Tag color="blue">{value}</Tag>,
    },
    {
      title: '触发条件',
      key: 'trigger',
      width: 180,
      render: (_: unknown, record: TaskSubtaskRelation) =>
        record.triggerField ? `${record.triggerField} = ${record.triggerValue || ''}` : '-',
    },
    {
      title: '处理标记',
      dataIndex: 'processedFlagField',
      key: 'processedFlagField',
      width: 160,
      render: (value: string | undefined) => value || '-',
    },
    {
      title: '回写字段',
      dataIndex: 'resultTargetField',
      key: 'resultTargetField',
      width: 140,
      render: (value: string | undefined) => value || '-',
    },
    {
      title: '启用',
      dataIndex: 'enabled',
      key: 'enabled',
      width: 100,
      render: (_: boolean, record: TaskSubtaskRelation) => (
        <Switch
          checked={record.enabled}
          size="small"
          onChange={(checked) => toggleTaskRelationEnabled(record, checked)}
        />
      ),
    },
    {
      title: '更新时间',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 180,
      render: (value: number | string | undefined) => formatDateTime(value),
    },
    {
      title: '操作',
      key: 'action',
      width: 180,
      fixed: 'right' as const,
      render: (_: unknown, record: TaskSubtaskRelation) => (
        <Space size={4}>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openEditRelationModal(record)}>
            编辑
          </Button>
          <Button type="link" size="small" icon={<CopyOutlined />} onClick={() => openDuplicateRelationModal(record)}>
            复制
          </Button>
        </Space>
      ),
    },
  ];

  const tabActionMap: Record<ActiveTabKey, () => void> = {
    definitions: openCreateDefinitionModal,
    categories: openCreateCategoryModal,
    relations: openCreateRelationModal,
  };

  const tabButtonTextMap: Record<ActiveTabKey, string> = {
    definitions: '新增任务定义',
    categories: '新增任务分类',
    relations: '新增子任务关系',
  };

  return (
    <div>
      <div
        style={{
          marginBottom: 16,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 8,
        }}
      >
        <div>
          <h1 style={{ margin: 0, fontSize: isMobile ? '18px' : '24px' }}>任务元数据管理</h1>
          <Text type="secondary">
            管理任务定义、任务分类和子任务关系，保存后任务元数据会自动刷新生效。
          </Text>
        </div>
        <Space wrap>
          <Button type="primary" icon={<PlusOutlined />} onClick={tabActionMap[activeTab]} size={isMobile ? 'small' : 'middle'}>
            {tabButtonTextMap[activeTab]}
          </Button>
          <Button icon={<ReloadOutlined />} onClick={refreshData} loading={loading} size={isMobile ? 'small' : 'middle'}>
            刷新列表
          </Button>
          <Button
            icon={<SyncOutlined />}
            onClick={handleRefreshRegistry}
            loading={refreshing}
            size={isMobile ? 'small' : 'middle'}
          >
            刷新注册中心
          </Button>
        </Space>
      </div>

      <Card>
        <Tabs
          activeKey={activeTab}
          onChange={(key) => setActiveTab(key as ActiveTabKey)}
          items={[
            {
              key: 'definitions',
              label: '任务定义',
              children: (
                <Table
                  rowKey="id"
                  loading={loading}
                  dataSource={taskDefinitions}
                  columns={definitionColumns}
                  scroll={{ x: 1200 }}
                  pagination={{
                    pageSize: isMobile ? 10 : 20,
                    showSizeChanger: !isMobile,
                    showQuickJumper: !isMobile,
                    showTotal: (total) => `共 ${total} 条记录`,
                    size: isMobile ? 'small' : 'default',
                  }}
                  size={isMobile ? 'small' : 'middle'}
                />
              ),
            },
            {
              key: 'categories',
              label: '任务分类',
              children: (
                <Table
                  rowKey="id"
                  loading={loading}
                  dataSource={taskCategories}
                  columns={categoryColumns}
                  scroll={{ x: 1100 }}
                  pagination={{
                    pageSize: isMobile ? 10 : 20,
                    showSizeChanger: !isMobile,
                    showQuickJumper: !isMobile,
                    showTotal: (total) => `共 ${total} 条记录`,
                    size: isMobile ? 'small' : 'default',
                  }}
                  size={isMobile ? 'small' : 'middle'}
                />
              ),
            },
            {
              key: 'relations',
              label: '子任务关系',
              children: (
                <Table
                  rowKey="id"
                  loading={loading}
                  dataSource={taskSubtaskRelations}
                  columns={relationColumns}
                  scroll={{ x: 1500 }}
                  pagination={{
                    pageSize: isMobile ? 10 : 20,
                    showSizeChanger: !isMobile,
                    showQuickJumper: !isMobile,
                    showTotal: (total) => `共 ${total} 条记录`,
                    size: isMobile ? 'small' : 'default',
                  }}
                  size={isMobile ? 'small' : 'middle'}
                />
              ),
            },
          ]}
        />
      </Card>

      <Modal
        title={editingDefinition ? '编辑任务定义' : '新增任务定义'}
        open={definitionModalVisible}
        onCancel={closeDefinitionModal}
        footer={null}
        width={isMobile ? '95%' : 640}
        style={{ top: isMobile ? 20 : 80 }}
      >
        <Form form={definitionForm} layout="vertical" onFinish={handleSaveDefinition}>
          <Form.Item name="id" hidden>
            <Input />
          </Form.Item>
          <Form.Item
            name="taskType"
            label="任务类型"
            rules={[
              { required: true, message: '请输入任务类型' },
              {
                validator: (_, value) => {
                  if (value === undefined || value === null) {
                    return Promise.resolve();
                  }
                  const duplicate = taskDefinitions.some(
                    (item) => item.taskType === Number(value) && item.id !== editingDefinition?.id
                  );
                  return duplicate ? Promise.reject(new Error('taskType 已存在')) : Promise.resolve();
                },
              },
            ]}
          >
            <InputNumber style={{ width: '100%' }} min={1} placeholder="例如：910001" />
          </Form.Item>
          <Form.Item
            name="taskKey"
            label="任务 Key"
            rules={[
              { required: true, message: '请输入任务 Key' },
              {
                pattern: TASK_KEY_PATTERN,
                message: 'taskKey 仅支持小写字母、数字和下划线',
              },
              {
                validator: (_, value) => {
                  if (!value) {
                    return Promise.resolve();
                  }
                  const duplicate = taskDefinitions.some(
                    (item) => item.taskKey === value && item.id !== editingDefinition?.id
                  );
                  return duplicate ? Promise.reject(new Error('taskKey 已存在')) : Promise.resolve();
                },
              },
            ]}
          >
            <Input placeholder="例如：demo_video_task" />
          </Form.Item>
          <Form.Item
            name="taskName"
            label="任务名称"
            rules={[{ required: true, message: '请输入任务名称' }]}
          >
            <Input placeholder="例如：DemoVideoTask" />
          </Form.Item>
          <Form.Item name="description" label="描述">
            <Input.TextArea rows={3} placeholder="请输入任务描述" />
          </Form.Item>
          <Form.Item name="categoryCode" label="任务分类">
            <Select
              allowClear
              showSearch
              placeholder="可选，归属某个任务分类"
              options={categoryOptions}
              optionFilterProp="label"
            />
          </Form.Item>
          <Form.Item name="priorityQueueEnabled" label="启用优先级队列" valuePropName="checked" initialValue={false}>
            <Switch />
          </Form.Item>
          <Form.Item name="enabled" label="是否启用" valuePropName="checked" initialValue={true}>
            <Switch />
          </Form.Item>
          <Form.Item
            name="operator"
            label="操作人"
            rules={[{ required: true, message: '请输入操作人' }]}
          >
            <Input placeholder="请输入操作人" />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={closeDefinitionModal}>取消</Button>
              <Button type="primary" htmlType="submit" loading={saving}>
                确定
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={editingCategory ? '编辑任务分类' : '新增任务分类'}
        open={categoryModalVisible}
        onCancel={closeCategoryModal}
        footer={null}
        width={isMobile ? '95%' : 640}
        style={{ top: isMobile ? 20 : 80 }}
      >
        <Form form={categoryForm} layout="vertical" onFinish={handleSaveCategory}>
          <Form.Item name="id" hidden>
            <Input />
          </Form.Item>
          <Form.Item
            name="categoryCode"
            label="分类编码"
            rules={[
              { required: true, message: '请输入分类编码' },
              {
                pattern: CATEGORY_CODE_PATTERN,
                message: 'categoryCode 仅支持字母、数字、下划线和短横线',
              },
              {
                validator: (_, value) => {
                  if (!value) {
                    return Promise.resolve();
                  }
                  const duplicate = taskCategories.some(
                    (item) => item.categoryCode === value && item.id !== editingCategory?.id
                  );
                  return duplicate ? Promise.reject(new Error('categoryCode 已存在')) : Promise.resolve();
                },
              },
            ]}
          >
            <Input placeholder="例如：video-demo" />
          </Form.Item>
          <Form.Item
            name="categoryName"
            label="分类名称"
            rules={[{ required: true, message: '请输入分类名称' }]}
          >
            <Input placeholder="例如：演示视频任务" />
          </Form.Item>
          <Form.Item name="description" label="描述">
            <Input.TextArea rows={3} placeholder="请输入分类描述" />
          </Form.Item>
          <Form.Item
            name="queueSuffix"
            label="队列后缀"
            extra="留空时默认使用 categoryCode。"
          >
            <Input placeholder="例如：video_demo" />
          </Form.Item>
          <Form.Item name="priorityEnabled" label="启用优先级队列" valuePropName="checked" initialValue={false}>
            <Switch />
          </Form.Item>
          <Form.Item name="enabled" label="是否启用" valuePropName="checked" initialValue={true}>
            <Switch />
          </Form.Item>
          <Form.Item
            name="operator"
            label="操作人"
            rules={[{ required: true, message: '请输入操作人' }]}
          >
            <Input placeholder="请输入操作人" />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={closeCategoryModal}>取消</Button>
              <Button type="primary" htmlType="submit" loading={saving}>
                确定
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={editingRelation ? '编辑子任务关系' : '新增子任务关系'}
        open={relationModalVisible}
        onCancel={closeRelationModal}
        footer={null}
        width={isMobile ? '95%' : 720}
        style={{ top: isMobile ? 20 : 60 }}
      >
        <Form form={relationForm} layout="vertical" onFinish={handleSaveRelation}>
          <Form.Item name="id" hidden>
            <Input />
          </Form.Item>
          <Form.Item
            name="relationName"
            label="关系名称"
            rules={[{ required: true, message: '请输入关系名称' }]}
          >
            <Input placeholder="例如：demo_needopt_relation" />
          </Form.Item>
          <Form.Item
            name="parentTaskType"
            label="父任务类型"
            rules={[{ required: true, message: '请选择父任务类型' }]}
          >
            <Select
              showSearch
              placeholder="请选择父任务"
              options={taskDefinitionOptions}
              optionFilterProp="label"
            />
          </Form.Item>
          <Form.Item
            name="subtaskTaskType"
            label="子任务类型"
            rules={[
              { required: true, message: '请选择子任务类型' },
              ({ getFieldValue }) => ({
                validator: (_, value) => {
                  const parentTaskType = getFieldValue('parentTaskType');
                  if (!value || !parentTaskType || value !== parentTaskType) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('父任务和子任务不能相同'));
                },
              }),
            ]}
          >
            <Select
              showSearch
              placeholder="请选择子任务"
              options={taskDefinitionOptions}
              optionFilterProp="label"
            />
          </Form.Item>
          <Form.Item
            name="relationType"
            label="关系类型"
            rules={[{ required: true, message: '请选择关系类型' }]}
          >
            <Select options={relationTypeOptions} placeholder="请选择关系类型" />
          </Form.Item>
          <Form.Item
            name="triggerField"
            label="触发字段"
            rules={
              relationType === 'NEEDOPT'
                ? [{ required: true, message: 'NEEDOPT 关系需要填写触发字段' }]
                : []
            }
          >
            <Input placeholder="例如：needopt" />
          </Form.Item>
          <Form.Item
            name="triggerValue"
            label="触发值"
            rules={
              relationType === 'NEEDOPT'
                ? [{ required: true, message: 'NEEDOPT 关系需要填写触发值' }]
                : []
            }
          >
            <Input placeholder="例如：true" />
          </Form.Item>
          <Form.Item
            name="processedFlagField"
            label="处理标记字段"
            rules={[{ required: true, message: '请输入处理标记字段' }]}
          >
            <Input placeholder="例如：needoptProcessed" />
          </Form.Item>
          <Form.Item
            name="resultTargetField"
            label="结果回写字段"
            rules={[{ required: true, message: '请输入结果回写字段' }]}
          >
            <Input placeholder="例如：img1" />
          </Form.Item>
          <Form.Item
            name="promptText"
            label="普通提示词"
            extra="主要用于 QWEN_NEXT，可按需填写。"
          >
            <Input.TextArea rows={3} placeholder="例如：make this image cleaner" />
          </Form.Item>
          <Form.Item
            name="promptTextWhenNeedopt"
            label="needopt 场景提示词"
            extra="主要用于 QWEN_NEXT + needopt=true 的场景。"
          >
            <Input.TextArea rows={3} placeholder="例如：make this image cleaner and optimize pose" />
          </Form.Item>
          <Form.Item name="resetTriggerAfterCompletion" label="完成后重置触发字段" valuePropName="checked" initialValue={false}>
            <Switch />
          </Form.Item>
          <Form.Item name="copyImg2" label="透传 img2" valuePropName="checked" initialValue={false}>
            <Switch />
          </Form.Item>
          <Form.Item name="enabled" label="是否启用" valuePropName="checked" initialValue={true}>
            <Switch />
          </Form.Item>
          <Form.Item
            name="operator"
            label="操作人"
            rules={[{ required: true, message: '请输入操作人' }]}
          >
            <Input placeholder="请输入操作人" />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={closeRelationModal}>取消</Button>
              <Button type="primary" htmlType="submit" loading={saving}>
                确定
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default TaskMetadataManagement;
